// Artwork lineage roots: upload a ChatGPT original with full provenance
// (concept + prompt links, provider/model/method/generated-at), and browse
// existing lineages.

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type { ArtworkAsset, Concept, PromptDraft } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function ArtworkPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [conceptId, setConceptId] = useState('');
  const [promptDraftId, setPromptDraftId] = useState('');
  const [provider, setProvider] = useState('chatgpt');
  const [model, setModel] = useState('');
  const [method, setMethod] = useState('manual_chat_ui');
  const [generatedAt, setGeneratedAt] = useState('');
  const [runRef, setRunRef] = useState('');
  const [uncertaintyNote, setUncertaintyNote] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const conceptsQuery = useQuery({
    queryKey: ['concepts', 'approved'],
    queryFn: () => api.get<Concept[]>('/concepts?status=approved&pageSize=100'),
  });
  const promptsQuery = useQuery({
    queryKey: ['prompt-drafts', conceptId],
    queryFn: () => api.get<PromptDraft[]>(`/prompt-drafts?conceptId=${conceptId}`),
    enabled: !!conceptId,
  });
  const listQuery = useQuery({
    queryKey: ['artwork', 'originals'],
    queryFn: () => api.get<ArtworkAsset[]>('/artwork?role=original&pageSize=100'),
  });

  const upload = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error('Please select a PNG file');
      if (!conceptId) throw new Error('Please select an approved concept');
      const form = new FormData();
      form.append('file', file);
      form.append('originalFilename', file.name);
      form.append('conceptId', conceptId);
      if (promptDraftId) form.append('promptDraftId', promptDraftId);
      form.append('provider', provider);
      form.append('method', method);
      if (model.trim()) form.append('model', model.trim());
      if (generatedAt) form.append('generatedAt', new Date(generatedAt).toISOString());
      if (runRef.trim()) form.append('generationRunReference', runRef.trim());
      if (uncertaintyNote.trim()) form.append('uncertaintyNote', uncertaintyNote.trim());
      if (note.trim()) form.append('note', note.trim());
      return api.upload<ArtworkAsset>('/artwork', form);
    },
    onSuccess: async () => {
      setError(null);
      if (fileRef.current) fileRef.current.value = '';
      setModel('');
      setRunRef('');
      setUncertaintyNote('');
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['artwork'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : String(err)),
  });

  const concepts = conceptsQuery.data?.data ?? [];
  const prompts = promptsQuery.data?.data ?? [];
  const originals = listQuery.data?.data ?? [];

  return (
    <section>
      <h1>Artwork</h1>
      <p className="muted">
        Upload a manually-generated original PNG and record its provenance. Everything downstream —
        normalization, edits, text rendering, and production — hangs off this immutable original.
      </p>

      <div className="card">
        <h2>Upload original</h2>
        <div className="form-row">
          <label htmlFor="aw-concept">Approved concept</label>
          <select id="aw-concept" value={conceptId} onChange={(e) => setConceptId(e.target.value)}>
            <option value="">Select a concept…</option>
            {concepts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="aw-prompt">Prompt version (optional)</label>
          <select
            id="aw-prompt"
            value={promptDraftId}
            onChange={(e) => setPromptDraftId(e.target.value)}
            disabled={!conceptId}
          >
            <option value="">No prompt link</option>
            {prompts.map((p) => (
              <option key={p.id} value={p.id}>
                v{p.version} — {p.prompt.slice(0, 60)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="aw-file">PNG file</label>
          <input id="aw-file" type="file" accept="image/png" ref={fileRef} />
        </div>
        <div className="form-row">
          <label htmlFor="aw-provider">Provider</label>
          <select id="aw-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="chatgpt">ChatGPT</option>
            <option value="openai">OpenAI</option>
            <option value="other">Other</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="aw-model">Model (if known)</label>
          <input
            id="aw-model"
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="e.g. gpt-image-1"
          />
        </div>
        <div className="form-row">
          <label htmlFor="aw-method">Method</label>
          <select id="aw-method" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="manual_chat_ui">Manual chat UI</option>
            <option value="native_api">Native API</option>
            <option value="other">Other</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="aw-generated">Generated at (if known)</label>
          <input
            id="aw-generated"
            type="datetime-local"
            value={generatedAt}
            onChange={(e) => setGeneratedAt(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label htmlFor="aw-runref">Generation reference (optional)</label>
          <input
            id="aw-runref"
            type="text"
            value={runRef}
            onChange={(e) => setRunRef(e.target.value)}
            placeholder="non-secret reference"
          />
        </div>
        <div className="form-row">
          <label htmlFor="aw-uncertainty">Uncertainty note (optional)</label>
          <input
            id="aw-uncertainty"
            type="text"
            value={uncertaintyNote}
            onChange={(e) => setUncertaintyNote(e.target.value)}
            placeholder="e.g. exact model unknown"
          />
        </div>
        <div className="form-row">
          <label htmlFor="aw-note">Note (optional)</label>
          <input id="aw-note" type="text" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button type="button" onClick={() => upload.mutate()} disabled={upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Upload original'}
        </button>
      </div>

      {originals.length === 0 && !listQuery.isLoading && (
        <p className="muted">No artwork uploaded yet.</p>
      )}
      <div className="card-grid">
        {originals.map((a) => (
          <Link key={a.id} to={`/artwork/${a.id}`} className="asset-card">
            <img src={`/api/v1/artwork/${a.id}/content`} alt="artwork original" />
            <StatusBadge status={a.status} />
            <span className="muted">{a.provenance.originalFilename}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
